import { useEffect, useState } from "react";
import { FileText, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, errMsg, download } from "../lib/api";
import { Btn, Card, Field, Input, Select, Money, Th, Td, Empty, Stat } from "./common";

const COLS = [["opening_cents", "Owed at start"], ["collected_cents", "Collected"], ["sent_cents", "Sent"], ["received_cents", "Confirmed received"], ["awaiting_cents", "Awaiting confirmation"], ["owed_cents", "Still owed"]];

function StatementTable({ st }) {
  if (!st.categories.length) return <Empty>No designated collections or remittances for this GKK up to this month.</Empty>;
  return (
    <div className="tbl-wrap">
      <table className="tbl" data-testid="statement-table">
        <thead><tr><Th>Designation</Th>{COLS.map(([k, l]) => <Th key={k} r>{l}</Th>)}</tr></thead>
        <tbody>
          {st.categories.map((r) => <tr key={r.category_id}><Td>{r.category_name}</Td>{COLS.map(([k]) => <Td key={k} r><Money c={r[k]} /></Td>)}</tr>)}
          <tr className="font-bold"><Td>TOTAL</Td>{COLS.map(([k]) => <Td key={k} r><Money c={st.totals[k]} testid={`statement-total-${k}`} /></Td>)}</tr>
        </tbody>
      </table>
    </div>
  );
}

export function MonthlyStatement() {
  const { user, meta, can } = useAuth();
  const [gkk, setGkk] = useState(user.gkk_id || meta.gkks[0]?.id || "");
  const [month, setMonth] = useState(meta.today.slice(0, 7));
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (gkk && month) api.get("/statements/monthly", { params: { gkk_id: gkk, month } }).then((r) => setSt(r.data)).catch((e) => { setSt(null); toast.error(errMsg(e)); });
  }, [gkk, month]);
  const pdf = async () => { setBusy("pdf"); try { await download("/statements/monthly/pdf", { gkk_id: gkk, month }, `remittance-statement-${month}.pdf`); } catch (e) { toast.error("Download failed"); } finally { setBusy(""); } };
  const remind = async () => { if (!window.confirm("Send a reminder now to every Unit and GKK treasurer in the parish who still holds unremitted designated collections?")) return; setBusy("r"); try { const { data } = await api.post("/reminders/unremitted"); toast.success(data.recipients ? `Reminder sent to ${data.recipients} treasurer(s)` : "No new reminders — treasurers were already reminded within the hour, or nothing is owed"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  return (
    <Card className="space-y-5 mt-4" data-testid="monthly-statement">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        {!user.gkk_id && <Field label="GKK" className="sm:w-80"><Select data-testid="statement-gkk-select" value={gkk} onChange={(e) => setGkk(e.target.value)}>{meta.gkks.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select></Field>}
        <Field label="Month"><Input data-testid="statement-month-input" type="month" max={meta.today.slice(0, 7)} value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
        <Btn v="primary" loading={busy === "pdf"} disabled={!st} onClick={pdf} data-testid="statement-pdf-button"><FileText className="h-5 w-5" /> Printable PDF</Btn>
        {can("reminders:send") && <Btn v="terra" loading={busy === "r"} onClick={remind} data-testid="unremitted-remind-button"><BellRing className="h-5 w-5" /> Remind treasurers now</Btn>}
      </div>
      {st && <>
        <h3 className="font-serif text-lg font-bold text-[#5C4033]" data-testid="statement-scope">{st.gkk_name}{st.unit_name ? ` · ${st.unit_name}` : ""}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Collected" c={st.totals.collected_cents} testid="statement-stat-collected" />
          <Stat label="Sent" c={st.totals.sent_cents} testid="statement-stat-sent" />
          <Stat label="Confirmed received" c={st.totals.received_cents} testid="statement-stat-received" note="Verified by the Parish Office" />
          <Stat label="Still owed" c={st.totals.owed_cents} testid="statement-stat-owed" tone={st.totals.owed_cents > 0 ? "bg-[#FDF3EC]" : ""} note={`As of ${st.date_to}`} />
        </div>
        <StatementTable st={st} />
        <p className="text-xs text-[#6E6259]">Treasurers get an email every Friday at 7:00 AM listing designated collections they still hold.</p>
      </>}
    </Card>
  );
}
